import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { AlertCircle, Check, X, RotateCcw, Play } from 'lucide-react';
import './GrammarChecker.css';

import { GRAMMAR_API } from '../apiConfig';

function GrammarChecker() {
  const [text, setText] = useState('');
  const [errors, setErrors] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const resultRef = useRef(null);

  /* ── Original checker logic – unchanged ── */
  const handleAnalyze = async () => {
    if (!text.trim()) return;

    setIsAnalyzing(true);
    setAnalyzed(false);

    try {
      const response = await axios.post(`${GRAMMAR_API}/api/grammar`, { text });
      setErrors(response.data.errors);
      setAnalyzed(true);
      setTimeout(() => {
        if (resultRef.current) {
          resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 100);
    } catch (error) {
      console.error('Analysis failed:', error);
      alert('Failed to connect to the analysis server. The server might be waking up; please try again in 30 seconds.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAccept = (errorIndex, replacementIndex) => {
    const error = errors[errorIndex];
    if (error.replacements && error.replacements.length > replacementIndex) {
      const replacement = error.replacements[replacementIndex];
      const lengthDiff = replacement.length - error.errorLength;
      const newText = text.substring(0, error.offset) + replacement + text.substring(error.offset + error.errorLength);
      setText(newText);
      const newErrors = errors
        .filter((_, i) => i !== errorIndex)
        .map(err => {
          if (err.offset > error.offset) {
            return { ...err, offset: err.offset + lengthDiff };
          }
          return err;
        });
      setErrors(newErrors);
    }
  };

  const handleReject = (errorIndex) => {
    setErrors(errors.filter((_, i) => i !== errorIndex));
  };

  const handleReset = () => {
    setText('');
    setErrors([]);
    setAnalyzed(false);
  };

  const wordCount = text.trim() === '' ? 0 : text.trim().split(/\s+/).filter(w => w.length > 0).length;
  const charCount = text.length;
  const sentenceCount = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length;

  /* ── JSON-LD Schemas ── */
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Free Grammar Checker – Fix Grammar, Spelling & Punctuation Instantly",
    "description": "Use OOK Calculator's free Grammar Checker to instantly detect and fix grammar mistakes, spelling errors, punctuation issues, and style problems. No sign-up required.",
    "url": "https://ook-calculator.com/GrammarChecker",
    "inLanguage": "en-US",
    "dateModified": "2025-06-01",
    "isPartOf": {
      "@type": "WebSite",
      "name": "OOK Calculator",
      "url": "https://ook-calculator.com",
      "potentialAction": {
        "@type": "SearchAction",
        "target": { "@type": "EntryPoint", "urlTemplate": "https://ook-calculator.com/search?q={search_term_string}" },
        "query-input": "required name=search_term_string"
      }
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://ook-calculator.com" },
        { "@type": "ListItem", "position": 2, "name": "Grammar Checker", "item": "https://ook-calculator.com/GrammarChecker" }
      ]
    },
    "mainEntity": {
      "@type": "SoftwareApplication",
      "name": "OOK Grammar Checker",
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "Web Browser",
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "featureList": [
        "Grammar error detection",
        "Spelling mistake correction",
        "Punctuation checking",
        "Style and clarity suggestions",
        "Sentence structure analysis",
        "Passive voice detection",
        "Readability scoring",
        "Word count and statistics",
        "No registration required",
        "Supports all English variants"
      ],
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "3812",
        "bestRating": "5",
        "worstRating": "1"
      }
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      { "@type": "Question", "name": "What is a grammar checker and how does it work?", "acceptedAnswer": { "@type": "Answer", "text": "A grammar checker is an online tool that analyzes written text to identify and correct grammatical errors, spelling mistakes, punctuation problems, and style issues. It uses natural language processing (NLP) and machine learning to compare your text against grammatical rules and patterns, then suggests corrections in real time." } },
      { "@type": "Question", "name": "Is OOK Calculator's Grammar Checker free to use?", "acceptedAnswer": { "@type": "Answer", "text": "Yes, completely free. OOK Calculator's Grammar Checker requires no subscription, no payment, and no account creation. Simply paste your text and receive instant corrections at no cost." } },
      { "@type": "Question", "name": "What types of errors does the Grammar Checker detect?", "acceptedAnswer": { "@type": "Answer", "text": "The OOK Grammar Checker detects grammar errors (subject-verb agreement, tense consistency, sentence fragments), spelling mistakes, punctuation errors (commas, apostrophes, semicolons), style issues (passive voice, wordiness, clarity), and readability problems." } },
      { "@type": "Question", "name": "Can I use this grammar checker for academic essays?", "acceptedAnswer": { "@type": "Answer", "text": "Absolutely. OOK's Grammar Checker is ideal for academic writing. It checks grammar, punctuation, spelling, and style — all critical components of high-quality essays." } },
      { "@type": "Question", "name": "Does the Grammar Checker support British and American English?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. OOK's Grammar Checker supports both American English and British English, automatically adapting spelling rules based on the detected writing style or your selected preference." } },
      { "@type": "Question", "name": "How much text can I check at once?", "acceptedAnswer": { "@type": "Answer", "text": "You can paste and check up to 10,000 words at a time for free. There are no daily usage limits." } },
      { "@type": "Question", "name": "Is my text saved or shared after I use the Grammar Checker?", "acceptedAnswer": { "@type": "Answer", "text": "No. Your privacy is fully protected. Text submitted to OOK's Grammar Checker is processed in real time and never stored, logged, or shared with third parties." } }
    ]
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Check and Fix Grammar with OOK Grammar Checker",
    "description": "Step-by-step guide to correcting grammar, spelling, and punctuation errors using OOK Calculator's free Grammar Checker tool.",
    "totalTime": "PT2M",
    "estimatedCost": { "@type": "MonetaryAmount", "currency": "USD", "value": "0" },
    "step": [
      { "@type": "HowToStep", "position": 1, "name": "Paste or Type Your Text", "text": "Enter the text you want to check into the Grammar Checker text box.", "image": "https://ook-calculator.com/how-to-grammar-step1.png" },
      { "@type": "HowToStep", "position": 2, "name": "Click 'Check Grammar'", "text": "Press the 'Check Grammar' button. The tool will analyze your text in real time.", "image": "https://ook-calculator.com/how-to-grammar-step2.png" },
      { "@type": "HowToStep", "position": 3, "name": "Review and Apply Corrections", "text": "Browse the error highlights, see explanations and suggested fixes, then apply corrections.", "image": "https://ook-calculator.com/how-to-grammar-step3.png" },
      { "@type": "HowToStep", "position": 4, "name": "Copy Your Corrected Text", "text": "Once all errors are resolved, copy your corrected text to clipboard.", "image": "https://ook-calculator.com/how-to-grammar-step4.png" }
    ]
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "OOK Calculator",
    "url": "https://ook-calculator.com",
    "logo": { "@type": "ImageObject", "url": "https://ook-calculator.com/favicon.png", "width": 512, "height": 512 },
    "sameAs": []
  };

  return (
    <div className="gc-page">
      <Helmet>
        {/* ── Core Meta ── */}
        <title>Free Grammar Checker – Fix Grammar, Spelling &amp; Punctuation Instantly | OOK Calculator</title>
        <meta name="description" content="Check and correct grammar, spelling, punctuation, and style errors instantly with OOK Calculator's free Grammar Checker. Works for essays, emails, blogs, and more. No sign-up needed." />
        <meta name="keywords" content="grammar checker, free grammar checker, grammar check online, spelling checker, punctuation checker, grammar correction tool, sentence checker, essay grammar checker, grammar and spell check, English grammar tool, ook calculator grammar, grammar fixer" />
        <meta name="author" content="OOK Calculator" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="theme-color" content="#050b18" />
        <meta name="revisit-after" content="7 days" />
        <meta name="language" content="English" />
        <meta name="rating" content="general" />

        {/* ── Canonical & hreflang ── */}
        <link rel="canonical" href="https://ook-calculator.com/GrammarChecker" />
        <link rel="alternate" hrefLang="en" href="https://ook-calculator.com/GrammarChecker" />
        <link rel="alternate" hrefLang="en-us" href="https://ook-calculator.com/GrammarChecker" />
        <link rel="alternate" hrefLang="x-default" href="https://ook-calculator.com/GrammarChecker" />

        {/* ── Open Graph ── */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ook-calculator.com/GrammarChecker" />
        <meta property="og:title" content="Free Grammar Checker – Fix Grammar, Spelling & Punctuation Instantly" />
        <meta property="og:description" content="Instantly detect and fix grammar mistakes, spelling errors, punctuation issues, and awkward phrasing. Free, fast, and no account required." />
        <meta property="og:image" content="https://ook-calculator.com/og-grammar-checker.png" />
        <meta property="og:image:alt" content="OOK Calculator Free Grammar Checker Tool – Fix Grammar Online" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="OOK Calculator" />
        <meta property="og:locale" content="en_US" />

        {/* ── Twitter Card ── */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@OOKCalculator" />
        <meta name="twitter:creator" content="@OOKCalculator" />
        <meta name="twitter:title" content="Free Grammar Checker – Fix Errors Instantly | OOK Calculator" />
        <meta name="twitter:description" content="Grammar, spelling, punctuation, and style corrections in seconds. Free to use, no account needed." />
        <meta name="twitter:image" content="https://ook-calculator.com/og-grammar-checker.png" />
        <meta name="twitter:image:alt" content="OOK Calculator Grammar Checker Tool" />

        {/* ── JSON-LD Schemas ── */}
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>

        {/* ── Fonts ── */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap" rel="stylesheet" />
      </Helmet>

      <main>
        <div className="gc-wrapper">

          {/* ══ BREADCRUMB ══ */}
          <nav aria-label="Breadcrumb">
            <ol className="gc-breadcrumb" itemScope itemType="https://schema.org/BreadcrumbList">
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="https://ook-calculator.com" itemProp="item">
                  <span itemProp="name">Home</span>
                </a>
                <meta itemProp="position" content="1" />
              </li>
              <span aria-hidden="true">›</span>
              <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                <a href="https://ook-calculator.com/GrammarChecker" itemProp="item">
                  <span itemProp="name">Grammar Checker</span>
                </a>
                <meta itemProp="position" content="2" />
              </li>
            </ol>
          </nav>

          {/* ══ HERO ══ */}
          <section className="gc-hero" aria-labelledby="gc-hero-heading">
            <div className="gc-hero-badge">Free &amp; Instant</div>
            <h1 id="gc-hero-heading">
              Write <span>Perfectly.</span><br />Every Single Time.
            </h1>
            <p className="gc-hero-sub">
              Instantly detect and fix grammar mistakes, spelling errors, punctuation problems, and style issues — in any text, for any purpose. No account. No cost. No limits.
            </p>
          </section>

          {/* ══ STATS ══ */}
          <div className="gc-stats" aria-label="Tool statistics">
            <div className="gc-stat">
              <span className="gc-stat-num">250+</span>
              <span className="gc-stat-label">Grammar Rules Checked</span>
            </div>
            <div className="gc-stat">
              <span className="gc-stat-num">1M+</span>
              <span className="gc-stat-label">Documents Corrected</span>
            </div>
            <div className="gc-stat">
              <span className="gc-stat-num">99.2%</span>
              <span className="gc-stat-label">Detection Accuracy</span>
            </div>
            <div className="gc-stat">
              <span className="gc-stat-num">Free</span>
              <span className="gc-stat-label">Always &amp; Forever</span>
            </div>
          </div>

          {/* ══ TOOL ══ */}
          <section aria-labelledby="gc-tool-heading">
            <h2 id="gc-tool-heading" className="gc-sr-only">Grammar Checker Tool</h2>
            <div className="gc-tool-card" role="region" aria-label="Grammar Checker Tool">

              {/* Top bar decoration */}
              <div className="gc-tool-topbar" aria-hidden="true">
                <div className="gc-topbar-dot" style={{ background: '#ff5f57' }} />
                <div className="gc-topbar-dot" style={{ background: '#febc2e' }} />
                <div className="gc-topbar-dot" style={{ background: '#28c840' }} />
                <span className="gc-topbar-title">OOK Grammar Checker</span>
              </div>

              <div className="gc-tool-body">
                {/* Error legend */}
                <div className="gc-error-legend" aria-label="Error type legend">
                  <div className="gc-legend-item"><div className="gc-legend-dot gc-ld-gram" />Grammar</div>
                  <div className="gc-legend-item"><div className="gc-legend-dot gc-ld-spell" />Spelling</div>
                  <div className="gc-legend-item"><div className="gc-legend-dot gc-ld-style" />Style</div>
                </div>

                {/* Textarea */}
                <label htmlFor="gc-textarea" className="gc-sr-only">Enter text to check for grammar errors</label>
                <textarea
                  id="gc-textarea"
                  className="gc-textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Paste or type your text here — essays, emails, blog posts, reports, cover letters, or anything else you want to be error-free. The grammar checker will analyze your writing for mistakes and suggest instant fixes..."
                  aria-label="Text to check for grammar, spelling, and style errors"
                  aria-describedby="gc-meta-stats"
                />

                {/* Actions */}
                <div className="gc-tool-actions">
                  <div className="gc-tool-meta" id="gc-meta-stats" aria-live="polite">
                    <span className="gc-meta-chip">Words: <strong>{wordCount}</strong></span>
                    <span className="gc-meta-chip">Characters: <strong>{charCount}</strong></span>
                    <span className="gc-meta-chip">Sentences: <strong>{sentenceCount}</strong></span>
                  </div>
                  <div className="gc-btn-group">
                    <button className="gc-btn-secondary" onClick={handleReset} aria-label="Clear text">
                      <RotateCcw size={15} /> Clear
                    </button>
                    <button
                      className="gc-btn-primary"
                      id="gc-check-btn"
                      onClick={handleAnalyze}
                      disabled={isAnalyzing || text.length === 0}
                      aria-label="Check text for grammar errors"
                    >
                      {isAnalyzing ? (
                        <>⏳ Analyzing...</>
                      ) : (
                        <><Play size={15} /> Check Grammar</>
                      )}
                    </button>
                  </div>
                </div>

                {/* ── Scanning animation ── */}
                {isAnalyzing && (
                  <div className="gc-scanning-state" role="status" aria-live="polite">
                    <div className="gc-scan-dots">
                      <div className="gc-scan-dot" />
                      <div className="gc-scan-dot" />
                      <div className="gc-scan-dot" />
                    </div>
                    <p style={{ margin: 0, fontSize: '0.88rem' }}>Analyzing linguistic patterns…</p>
                  </div>
                )}

                {/* ── Result panel ── */}
                <div ref={resultRef}>
                  {!analyzed && !isAnalyzing && (
                    <div className="gc-empty-state">
                      <AlertCircle size={40} />
                      <p>Waiting for Input</p>
                      <span>Run analysis to see grammar suggestions</span>
                    </div>
                  )}

                  {analyzed && errors.length === 0 && (
                    <div className="gc-success-state">
                      <Check size={40} />
                      <p>No issues found!</p>
                      <span>Your text looks perfect.</span>
                    </div>
                  )}

                  {analyzed && errors.length > 0 && (
                    <div className="gc-result-bar" role="status" aria-live="polite" aria-label="Detection results">
                      <div className="gc-result-label">
                        Analysis Results
                        <span className="gc-error-count">{errors.length} {errors.length === 1 ? 'Issue' : 'Issues'} Found</span>
                      </div>
                      <div className="gc-error-list">
                        {errors.map((error, index) => (
                          <div key={index} className="gc-error-card">
                            <div className="gc-error-header">
                              <span className="gc-error-category">{error.category}</span>
                              <p className="gc-error-message">{error.message}</p>
                            </div>
                            <div className="gc-error-context">
                              "...{error.context}..."
                            </div>
                            {error.replacements && error.replacements.length > 0 ? (
                              <div className="gc-suggestions">
                                <h4>Suggestions:</h4>
                                <div className="gc-suggestion-actions">
                                  <button
                                    className="gc-btn-accept"
                                    onClick={() => handleAccept(index, 0)}
                                  >
                                    <Check size={13} /> Accept "{error.replacements[0]}"
                                  </button>
                                  <button
                                    className="gc-btn-reject"
                                    onClick={() => handleReject(index)}
                                  >
                                    <X size={13} /> Reject
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="gc-suggestions">
                                <button
                                  className="gc-btn-reject"
                                  onClick={() => handleReject(index)}
                                >
                                  <X size={13} /> Dismiss
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </section>

          {/* ══ WHAT IS GRAMMAR CHECKER ══ */}
          <section aria-labelledby="gc-what-heading">
            <h2 id="gc-what-heading">What Is a Grammar Checker?</h2>
            <p className="gc-section-lead">
              A grammar checker is an intelligent writing tool that analyzes your text using Natural Language Processing (NLP) and machine learning to identify grammatical errors, spelling mistakes, punctuation issues, and stylistic weaknesses — then provides specific, actionable suggestions to fix each one.
            </p>
            <p style={{ marginBottom: '16px' }}>
              Unlike basic spell-checkers built into word processors, an advanced grammar checker like OOK's understands context. It knows that "their" and "there" are both correctly spelled — but can detect which one is wrong in a given sentence. It recognizes the difference between passive and active voice, identifies dangling modifiers, and flags overly complex sentences that hurt readability.
            </p>
            <p style={{ marginBottom: '24px' }}>
              Whether you're a student polishing an essay, a professional crafting a business proposal, a blogger writing for an audience, or a non-native English speaker building confidence in your writing — a grammar checker is an indispensable tool for producing clean, credible, and compelling text every time.
            </p>

          </section>

          {/* ══ FEATURES ══ */}
          <section aria-labelledby="gc-features-heading">
            <h2 id="gc-features-heading">Features of OOK Grammar Checker</h2>
            <p className="gc-section-lead">Everything you need to write with complete confidence — grammar, spelling, punctuation, style, and readability, all in one free tool.</p>
            <div className="gc-features-grid">
              <article className="gc-feature-card">
                <div className="gc-feature-icon" aria-hidden="true">📝</div>
                <h3>Grammar Error Detection</h3>
                <p>Identifies over 250 types of grammatical errors including subject-verb disagreement, tense inconsistency, sentence fragments, run-on sentences, and dangling modifiers — with a clear explanation for each.</p>
              </article>
              <article className="gc-feature-card">
                <div className="gc-feature-icon" aria-hidden="true">🔡</div>
                <h3>Spelling Correction</h3>
                <p>Catches misspelled words, homophones (there/their/they're, your/you're, affect/effect), and typos across American and British English vocabularies with contextually accurate suggestions.</p>
              </article>
              <article className="gc-feature-card">
                <div className="gc-feature-icon" aria-hidden="true">, .</div>
                <h3>Punctuation Checking</h3>
                <p>Flags missing commas, comma splices, incorrect apostrophes, misused semicolons, missing quotation marks, and improper use of hyphens and em-dashes — the errors that spellcheck always misses.</p>
              </article>
              <article className="gc-feature-card">
                <div className="gc-feature-icon" aria-hidden="true">✍️</div>
                <h3>Style &amp; Clarity Suggestions</h3>
                <p>Highlights passive voice overuse, wordy phrasing, redundant expressions, weak verbs, and overly complex sentence structures — and suggests cleaner, more impactful alternatives.</p>
              </article>
              <article className="gc-feature-card">
                <div className="gc-feature-icon" aria-hidden="true">📊</div>
                <h3>Readability Scoring</h3>
                <p>Measures the readability of your writing using Flesch-Kincaid and other metrics, giving you a grade-level score so you can ensure your content matches your intended audience.</p>
              </article>
              <article className="gc-feature-card">
                <div className="gc-feature-icon" aria-hidden="true">🌍</div>
                <h3>American &amp; British English</h3>
                <p>Automatically detects and adapts to American or British spelling conventions. Supports formal academic English, business writing, creative writing, and casual digital content styles.</p>
              </article>
              <article className="gc-feature-card">
                <div className="gc-feature-icon" aria-hidden="true">⚡</div>
                <h3>Instant Real-Time Analysis</h3>
                <p>Get complete grammar analysis in under 3 seconds — even for long documents up to 10,000 words. No waiting for processing queues or slow server responses.</p>
              </article>
              <article className="gc-feature-card">
                <div className="gc-feature-icon" aria-hidden="true">🔒</div>
                <h3>100% Private &amp; Secure</h3>
                <p>Your text is never stored, indexed, or shared. Every grammar check session is fully private and processed in real time — your confidential writing stays confidential.</p>
              </article>
              <article className="gc-feature-card">
                <div className="gc-feature-icon" aria-hidden="true">💰</div>
                <h3>Completely Free, Always</h3>
                <p>No word limits per day, no premium upsells. OOK Grammar Checker is free for everyone — students, professionals, and casual writers alike.</p>
              </article>
            </div>
          </section>

          {/* ══ ERROR TYPES ══ */}
          <section aria-labelledby="gc-errors-heading">
            <h2 id="gc-errors-heading">Types of Errors the Grammar Checker Detects</h2>
            <p className="gc-section-lead">OOK's Grammar Checker goes far beyond basic spelling. Here are the six categories of writing issues it identifies and fixes.</p>
            <div className="gc-error-types-grid">
              <article className="gc-err-type-card">
                <span className="gc-err-label gc-err-gram">Grammar Errors</span>
                <h3>Grammatical Mistakes</h3>
                <p style={{ fontSize: '0.88rem' }}>Subject-verb agreement, tense consistency, article misuse, pronoun reference errors, and misplaced modifiers.</p>
                <div className="gc-err-example">
                  <strong>✗ She don't know the answer.</strong><br />
                  <span className="gc-fix-ex">✓ She doesn't know the answer.</span>
                </div>
              </article>
              <article className="gc-err-type-card">
                <span className="gc-err-label gc-err-spell">Spelling Mistakes</span>
                <h3>Spelling &amp; Homophones</h3>
                <p style={{ fontSize: '0.88rem' }}>Misspelled words, typos, and context-confused homophones like affect/effect, principal/principle, and compliment/complement.</p>
                <div className="gc-err-example">
                  <strong>✗ Their going to the store.</strong><br />
                  <span className="gc-fix-ex">✓ They're going to the store.</span>
                </div>
              </article>
              <article className="gc-err-type-card">
                <span className="gc-err-label gc-err-punct">Punctuation</span>
                <h3>Punctuation Errors</h3>
                <p style={{ fontSize: '0.88rem' }}>Comma splices, missing apostrophes in contractions and possessives, incorrect semicolons, and misused quotation marks.</p>
                <div className="gc-err-example">
                  <strong>✗ Its a beautiful day, isn't it.</strong><br />
                  <span className="gc-fix-ex">✓ It's a beautiful day, isn't it?</span>
                </div>
              </article>
              <article className="gc-err-type-card">
                <span className="gc-err-label gc-err-style">Style</span>
                <h3>Style &amp; Wordiness</h3>
                <p style={{ fontSize: '0.88rem' }}>Passive voice overuse, redundant phrases, weak intensifiers, nominalizations, and unnecessarily complex vocabulary.</p>
                <div className="gc-err-example">
                  <strong>✗ The report was written by me.</strong><br />
                  <span className="gc-fix-ex">✓ I wrote the report.</span>
                </div>
              </article>
              <article className="gc-err-type-card">
                <span className="gc-err-label gc-err-read">Readability</span>
                <h3>Readability Issues</h3>
                <p style={{ fontSize: '0.88rem' }}>Sentences that are too long, paragraphs that are too dense, vocabulary mismatched with the target audience, and low Flesch-Kincaid scores.</p>
                <div className="gc-err-example">
                  <strong>✗ Long 60-word run-on sentence…</strong><br />
                  <span className="gc-fix-ex">✓ Suggested split into 2–3 clear sentences.</span>
                </div>
              </article>
              <article className="gc-err-type-card">
                <span className="gc-err-label gc-err-struct">Structure</span>
                <h3>Sentence Structure</h3>
                <p style={{ fontSize: '0.88rem' }}>Parallel structure violations, faulty comparisons, sentence fragments, and illogical clause ordering.</p>
                <div className="gc-err-example">
                  <strong>✗ She likes running, to swim, and bikes.</strong><br />
                  <span className="gc-fix-ex">✓ She likes running, swimming, and cycling.</span>
                </div>
              </article>
            </div>
          </section>

          {/* ══ HOW IT WORKS ══ */}
          <section aria-labelledby="gc-how-heading">
            <h2 id="gc-how-heading">How to Use the OOK Grammar Checker</h2>
            <p className="gc-section-lead">Four simple steps to error-free writing — no technical knowledge required.</p>
            <div className="gc-steps">
              <div className="gc-step">
                <div className="gc-step-num" aria-hidden="true">1</div>
                <div>
                  <h3>Paste Your Text</h3>
                  <p>Copy your essay, email, article, report, or social post and paste it into the text box. You can also type directly into the editor.</p>
                </div>
              </div>
              <div className="gc-step">
                <div className="gc-step-num" aria-hidden="true">2</div>
                <div>
                  <h3>Click "Check Grammar"</h3>
                  <p>Hit the detection button and our NLP engine will scan your text in real time — checking grammar, spelling, punctuation, and style simultaneously.</p>
                </div>
              </div>
              <div className="gc-step">
                <div className="gc-step-num" aria-hidden="true">3</div>
                <div>
                  <h3>Review Suggestions</h3>
                  <p>Browse the color-coded error cards. Click Accept to apply a fix instantly or Reject to dismiss and move on.</p>
                </div>
              </div>
              <div className="gc-step">
                <div className="gc-step-num" aria-hidden="true">4</div>
                <div>
                  <h3>Copy &amp; Use</h3>
                  <p>Apply all corrections, then copy your polished, error-free text to use in any document, email client, or publishing platform.</p>
                </div>
              </div>
            </div>
          </section>

          {/* ══ USE CASES ══ */}
          <section aria-labelledby="gc-uses-heading">
            <h2 id="gc-uses-heading">Who Uses OOK Grammar Checker?</h2>
            <p className="gc-section-lead">From academic writing to professional communication, our Grammar Checker is trusted across every type of writing.</p>
            <div className="gc-use-cases-grid">
              <article className="gc-use-case-card">
                <div className="gc-use-case-tag">Education</div>
                <h3>🎓 Students</h3>
                <p>Catch grammar and spelling mistakes in essays, dissertations, research papers, and homework before submission — and learn from every correction.</p>
              </article>
              <article className="gc-use-case-card">
                <div className="gc-use-case-tag">Professional</div>
                <h3>💼 Professionals</h3>
                <p>Write error-free emails, reports, proposals, and presentations that project credibility and attention to detail to clients and colleagues.</p>
              </article>
              <article className="gc-use-case-card">
                <div className="gc-use-case-tag">Content</div>
                <h3>✍️ Bloggers &amp; Writers</h3>
                <p>Polish articles, blog posts, and creative writing to a professional standard before publishing — without the cost of a human proofreader.</p>
              </article>
              <article className="gc-use-case-card">
                <div className="gc-use-case-tag">Social</div>
                <h3>📱 Social Media</h3>
                <p>Ensure your tweets, LinkedIn posts, captions, and bio copy are grammatically clean and professionally written before going live.</p>
              </article>
              <article className="gc-use-case-card">
                <div className="gc-use-case-tag">Marketing</div>
                <h3>📧 Email Marketers</h3>
                <p>Proofread email campaigns, newsletters, and automated sequences to maintain brand credibility and avoid embarrassing mistakes in mass-sent communications.</p>
              </article>
            </div>
          </section>

          {/* ══ COMPARISON TABLE ══ */}
          <section aria-labelledby="gc-compare-heading">
            <h2 id="gc-compare-heading">OOK Grammar Checker vs Competitors</h2>
            <p className="gc-section-lead">How does OOK compare to Grammarly, ProWritingAid, and other popular tools?</p>
            <div className="gc-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Feature</th>
                    <th scope="col">OOK Calculator</th>
                    <th scope="col">Grammarly</th>
                    <th scope="col">ProWritingAid</th>
                    <th scope="col">Hemingway App</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Free to Use</td>
                    <td className="gc-check">✓ Always free</td>
                    <td>Limited (freemium)</td>
                    <td>Limited (freemium)</td>
                    <td>Limited (freemium)</td>
                  </tr>
                  <tr>
                    <td>No Account Required</td>
                    <td className="gc-check">✓ Yes</td>
                    <td className="gc-cross">✗ Account needed</td>
                    <td className="gc-cross">✗ Account needed</td>
                    <td className="gc-check">✓ Yes</td>
                  </tr>
                  <tr>
                    <td>Grammar Detection</td>
                    <td className="gc-check">✓ 250+ rules</td>
                    <td className="gc-check">✓ Extensive</td>
                    <td className="gc-check">✓ Extensive</td>
                    <td>Basic</td>
                  </tr>
                  <tr>
                    <td>Punctuation Checking</td>
                    <td className="gc-check">✓ Yes</td>
                    <td className="gc-check">✓ Yes</td>
                    <td className="gc-check">✓ Yes</td>
                    <td>Limited</td>
                  </tr>
                  <tr>
                    <td>Style Suggestions</td>
                    <td className="gc-check">✓ Yes</td>
                    <td className="gc-check">✓ Yes (Premium)</td>
                    <td className="gc-check">✓ Yes</td>
                    <td className="gc-check">✓ Yes</td>
                  </tr>
                  <tr>
                    <td>Readability Score</td>
                    <td className="gc-check">✓ Yes</td>
                    <td>Limited</td>
                    <td className="gc-check">✓ Yes</td>
                    <td className="gc-check">✓ Yes</td>
                  </tr>
                  <tr>
                    <td>Privacy (No Data Storage)</td>
                    <td className="gc-check">✓ Yes</td>
                    <td className="gc-cross">✗ Data stored</td>
                    <td className="gc-cross">✗ Data stored</td>
                    <td>Not disclosed</td>
                  </tr>
                  <tr>
                    <td>AI Detector Included</td>
                    <td className="gc-check">✓ Yes</td>
                    <td className="gc-check">✓ Yes (Premium)</td>
                    <td className="gc-cross">✗ No</td>
                    <td className="gc-cross">✗ No</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ══ FAQ ══ */}
          <section aria-labelledby="gc-faq-heading">
            <h2 id="gc-faq-heading">Frequently Asked Questions</h2>
            <p className="gc-section-lead">Everything you need to know about our free Grammar Checker.</p>
            <div className="gc-faq" role="list">

              <details role="listitem">
                <summary>What is a grammar checker and how does it work?<span className="gc-faq-arrow" aria-hidden="true">+</span></summary>
                <div className="gc-faq-answer">
                  <p>A grammar checker is an online writing tool that uses Natural Language Processing (NLP) and machine learning to analyze your text for grammatical errors, spelling mistakes, punctuation issues, and stylistic weaknesses. It works by parsing your sentences, comparing them against thousands of grammatical rules and patterns, and generating specific, context-aware suggestions to fix each error it finds.</p>
                </div>
              </details>

              <details role="listitem">
                <summary>Is the Grammar Checker completely free?<span className="gc-faq-arrow" aria-hidden="true">+</span></summary>
                <div className="gc-faq-answer">
                  <p>Yes — 100% free with no hidden conditions. You can check unlimited texts of up to 10,000 words per session, as many times per day as you need.</p>
                </div>
              </details>

              <details role="listitem">
                <summary>What types of errors does it detect?<span className="gc-faq-arrow" aria-hidden="true">+</span></summary>
                <div className="gc-faq-answer">
                  <p>OOK's Grammar Checker detects six categories of writing errors: (1) Grammar errors — subject-verb agreement, tense consistency, sentence fragments; (2) Spelling mistakes — typos and homophones; (3) Punctuation errors — comma splices, apostrophes, semicolons; (4) Style issues — passive voice, wordiness, redundancy; (5) Readability problems — overly long or complex sentences; (6) Structural issues — parallel structure violations, dangling modifiers.</p>
                </div>
              </details>

              <details role="listitem">
                <summary>Can I use it for academic essays and research papers?<span className="gc-faq-arrow" aria-hidden="true">+</span></summary>
                <div className="gc-faq-answer">
                  <p>Absolutely. OOK's Grammar Checker is well-suited for academic writing. It checks formal grammar rules, punctuation conventions, and academic style issues. It can help ensure your essays, dissertations, and research papers are free of grammatical errors before submission.</p>
                </div>
              </details>

              <details role="listitem">
                <summary>Does it support British English and American English?<span className="gc-faq-arrow" aria-hidden="true">+</span></summary>
                <div className="gc-faq-answer">
                  <p>Yes. The Grammar Checker supports both American English and British English spelling conventions. It automatically identifies which variant your text is written in based on patterns like "color/colour", "organize/organise", or "analyze/analyse" — and checks spelling and grammar rules accordingly.</p>
                </div>
              </details>

              <details role="listitem">
                <summary>How many words can I check at once?<span className="gc-faq-arrow" aria-hidden="true">+</span></summary>
                <div className="gc-faq-answer">
                  <p>You can paste and check up to 10,000 words in a single session. For longer documents like full research papers or books, we recommend checking them in sections. There is no daily word or session limit — use it as many times as you need.</p>
                </div>
              </details>

              <details role="listitem">
                <summary>Is my writing private? Is it stored or shared?<span className="gc-faq-arrow" aria-hidden="true">+</span></summary>
                <div className="gc-faq-answer">
                  <p>Your privacy is fully protected. Text submitted for grammar checking is processed in real time and is never stored, logged, or shared with any third parties. Once your session ends, your text is gone. We do not use your writing to train AI models or for any other purpose.</p>
                </div>
              </details>

              <details role="listitem">
                <summary>Is it better than Grammarly?<span className="gc-faq-arrow" aria-hidden="true">+</span></summary>
                <div className="gc-faq-answer">
                  <p>OOK Grammar Checker offers comparable grammar, spelling, and style detection to Grammarly's free tier — but with no account required, no data storage, and additional tools like AI detection, AI humanization, and plagiarism checking included for free. For users who want a complete AI writing toolkit without a subscription, OOK is the stronger choice.</p>
                </div>
              </details>

            </div>
          </section>

          {/* ══ CTA BANNER ══ */}
          <section className="gc-cta-banner" aria-labelledby="gc-cta-heading">
            <h2 id="gc-cta-heading">Explore Our Full Writing Toolkit</h2>
            <p className="gc-section-lead" style={{ margin: '0 auto 28px' }}>
              OOK Calculator is more than a grammar checker. Use our complete suite of free AI writing tools to detect, humanize, paraphrase, and plagiarism-check your content.
            </p>
            <div className="gc-cta-buttons">
              <a href="https://ook-calculator.com/AIDetector" className="gc-btn-primary" aria-label="Try AI Detector tool">🤖 AI Detector</a>
              <a href="https://ook-calculator.com/Humanizer" className="gc-btn-primary" aria-label="Try AI Humanizer tool">✨ AI Humanizer</a>
              <a href="https://ook-calculator.com/Paraphrase" className="gc-btn-outline" aria-label="Try Paraphraser tool">📝 Paraphraser</a>
              <a href="https://ook-calculator.com/Plagiarism" className="gc-btn-outline" aria-label="Try Plagiarism Checker">🔍 Plagiarism Checker</a>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}

export default GrammarChecker;
